import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { ChineseService, PracticeExercise } from '../../services/chineseService';
import { LessonService, GenericLesson, LessonSummary } from '../../services/lessonServiceTypes';
import PageHeader from '../common/PageHeader';
import GenericPastLessons from '../shared/GenericPastLessons';
import './ChinesePracticeTranslation.css';

interface ChinesePracticeTranslationProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type Difficulty = 'HSK 3-4' | 'Beginner' | 'Intermediate' | 'Advanced';
type ViewMode = 'current' | 'past-list' | 'past-exercise';

interface SentenceFeedback {
  english: string;
  correct: string;
  pinyin: string;
  user: string;
  feedback: string;
}

interface EvaluationResult {
  feedback: string;
  sentences: SentenceFeedback[];
}

// Service wrapper to adapt practice exercises to LessonService interface
class PracticeExerciseService implements LessonService<GenericLesson> {
  async getPastLessons(limit: number = 50, offset: number = 0): Promise<{
    lessons: LessonSummary[];
    total: number;
  }> {
    const result = await ChineseService.getPastPracticeExercises(limit, offset);
    return {
      lessons: result.exercises,
      total: result.total
    };
  }

  async getLessonById(lessonId: string): Promise<GenericLesson> {
    const exercise = await ChineseService.getPracticeExerciseById(lessonId);
    // Convert PracticeExercise to GenericLesson format
    // Use stored lesson_name and lesson_objective if available, otherwise generate fallback
    return {
      lesson_id: exercise.lesson_id,
      lesson_name: exercise.lesson_name || `Translation Practice - ${exercise.difficulty}`,
      lesson_objective: exercise.lesson_objective || `Practice translating English text to Chinese at ${exercise.difficulty} level`,
      category: 'chinese_practice_translation',
      created_at: exercise.created_at,
      updated_at: exercise.updated_at
    };
  }

  async deleteLesson(lessonId: string): Promise<void> {
    await ChineseService.deleteLesson(lessonId);
  }
}

const ChinesePracticeTranslation: React.FC<ChinesePracticeTranslationProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const navigate = useNavigate();
  const [difficulty, setDifficulty] = useState<Difficulty>('HSK 3-4');
  const [englishText, setEnglishText] = useState<string>('');
  const [userTranslation, setUserTranslation] = useState<string>('');
  const [evaluationResult, setEvaluationResult] = useState<EvaluationResult | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isGeneratingAnswer, setIsGeneratingAnswer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingError, setSavingError] = useState<string | null>(null);
  const [lessonId, setLessonId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const [selectedPastExercise, setSelectedPastExercise] = useState<PracticeExercise | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [pastExercisesList, setPastExercisesList] = useState<LessonSummary[]>([]);
  const [generatedAnswer, setGeneratedAnswer] = useState<{ translation: string; pinyin: string } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const practiceExerciseService = new PracticeExerciseService();

  // Load latest exercise from database on mount
  useEffect(() => {
    loadLatestExercise();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const loadLatestExercise = async () => {
    try {
      const result = await ChineseService.getPastPracticeExercises(1);
      if (result.exercises.length > 0) {
        const latestExercise = await ChineseService.getPracticeExerciseById(result.exercises[0].lesson_id);
        setEnglishText(latestExercise.englishText);
        setDifficulty(latestExercise.difficulty as Difficulty);
        setUserTranslation(latestExercise.userTranslation || '');
        setEvaluationResult(latestExercise.evaluationResult || null);
        setLessonId(latestExercise.lesson_id);
      }
    } catch (err) {
      console.error('Error loading latest exercise:', err);
      // It's okay if there are no exercises yet
    }
  };

  const handleOpenPastExercises = async () => {
    try {
      setShowMenu(false);
      // Fetch past exercises for the list
      const result = await ChineseService.getPastPracticeExercises(100);
      setPastExercisesList(result.exercises);
      setViewMode('past-list');
    } catch (err) {
      console.error('Error loading past exercises:', err);
      // Open list anyway, it will show empty state
      setPastExercisesList([]);
      setViewMode('past-list');
    }
  };

  const handleSelectPastExercise = async (genericLesson: GenericLesson) => {
    try {
      const exercise = await ChineseService.getPracticeExerciseById(genericLesson.lesson_id!);
      setSelectedPastExercise(exercise);
      setViewMode('past-exercise');
    } catch (err) {
      console.error('Error loading past exercise:', err);
      setError(err instanceof Error ? err.message : 'Failed to load exercise');
    }
  };

  const handleBackToCurrentExercise = () => {
    setViewMode('current');
    setSelectedPastExercise(null);
  };

  const handleRedoExercise = () => {
    if (!selectedPastExercise) return;
    
    // Load the past exercise data into current state
    setEnglishText(selectedPastExercise.englishText);
    setDifficulty(selectedPastExercise.difficulty as Difficulty);
    setLessonId(selectedPastExercise.lesson_id);
    setUserTranslation('');
    setEvaluationResult(null);
    
    // Clear error states
    setError(null);
    setSavingError(null);
    
    // Switch to current view mode
    setViewMode('current');
    setSelectedPastExercise(null);
  };

  const generateExercise = async () => {
    try {
      setIsGenerating(true);
      setError(null);
      setSavingError(null);
      setUserTranslation('');
      setEvaluationResult(null);
      setLessonId(null);
      setGeneratedAnswer(null);

      const result = await ChineseService.generatePracticeExercise(difficulty);
      setEnglishText(result.englishText);

      // Save exercise to database
      try {
        const saveResult = await ChineseService.savePracticeExercise(
          result.englishText,
          difficulty
        );
        setLessonId(saveResult.lesson_id);
      } catch (saveErr) {
        console.error('Error saving exercise:', saveErr);
        setSavingError('Exercise generated but failed to save. You can still practice.');
      }
    } catch (err) {
      console.error('Error generating exercise:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate exercise. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateNew = async () => {
    await generateExercise();
    setGeneratedAnswer(null);
  };

  const handleGenerateAnswer = async () => {
    if (!englishText.trim()) {
      setError('Please generate an exercise first.');
      return;
    }

    try {
      setIsGeneratingAnswer(true);
      setError(null);

      const result = await ChineseService.generateAnswer(englishText, difficulty);
      setGeneratedAnswer(result);
    } catch (err) {
      console.error('Error generating answer:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate answer. Please try again.');
    } finally {
      setIsGeneratingAnswer(false);
    }
  };

  const handleSubmit = async () => {
    if (!userTranslation.trim()) {
      setError('Please enter your translation before submitting.');
      return;
    }

    try {
      setIsEvaluating(true);
      setError(null);
      setSavingError(null);

      const result = await ChineseService.evaluateTranslation(
        englishText,
        userTranslation.trim(),
        difficulty
      );
      setEvaluationResult(result);

      // Save evaluation result to database
      try {
        await ChineseService.savePracticeExercise(
          englishText,
          difficulty,
          userTranslation.trim(),
          result,
          lessonId || undefined
        );
      } catch (saveErr) {
        console.error('Error saving evaluation:', saveErr);
        setSavingError('Evaluation completed but failed to save. Your feedback is still available.');
      }
    } catch (err) {
      console.error('Error evaluating translation:', err);
      setError(err instanceof Error ? err.message : 'Failed to evaluate translation. Please try again.');
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleReset = () => {
    setUserTranslation('');
    setEvaluationResult(null);
    setError(null);
    setGeneratedAnswer(null);
    // Keep englishText so user can resubmit
  };

  // Get the current exercise being displayed
  const getCurrentExercise = (): PracticeExercise | null => {
    if (viewMode === 'past-exercise' && selectedPastExercise) {
      return selectedPastExercise;
    }
    if (englishText && lessonId) {
      return {
        lesson_id: lessonId,
        englishText,
        difficulty,
        userTranslation: userTranslation || undefined,
        evaluationResult: evaluationResult || undefined,
        created_at: '',
        updated_at: ''
      };
    }
    return null;
  };

  const currentExercise = getCurrentExercise();
  const showBackButton = viewMode === 'past-exercise';

  return (
    <div className="chinese-practice-translation-container">
      {/* If in past exercises list view, show GenericPastLessons component */}
      {viewMode === 'past-list' && (
        <GenericPastLessons
          service={practiceExerciseService}
          onSelectLesson={handleSelectPastExercise}
          onClose={handleBackToCurrentExercise}
          title="Past Exercises"
        />
      )}

      {/* Main practice view */}
      {viewMode !== 'past-list' && (
        <>
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        leftActions={
          showBackButton ? (
            <button 
              className="page-header-icon-btn chinese-practice-translation-back-btn"
              onClick={handleBackToCurrentExercise}
              aria-label="Back to current exercise"
            >
              ←
            </button>
          ) : (
            <button 
              className="page-header-icon-btn chinese-practice-translation-back-btn"
              onClick={() => navigate('/chinese')}
              aria-label="Back to Chinese Learning"
            >
              ←
            </button>
          )
        }
        centerContent={
          <h1 className="page-header-center-title">
            {viewMode === 'past-exercise' ? 'Past Exercise' : 'Translation Practice'}
          </h1>
        }
        rightActions={
          <div className="chinese-practice-translation-menu-container" ref={menuRef}>
            <button 
              className="page-header-icon-btn"
              onClick={() => setShowMenu(!showMenu)}
              disabled={isGenerating || isEvaluating}
            >
              ⋮
            </button>
            {showMenu && (
              <div className="chinese-practice-translation-menu-dropdown">
                <button
                  className="chinese-practice-translation-menu-item"
                  onClick={handleOpenPastExercises}
                >
                  📚 Past Exercises
                </button>
              </div>
            )}
          </div>
        }
      />

      <div className="chinese-practice-translation-content">
        {/* Difficulty Selector - Only show for current exercise */}
        {viewMode !== 'past-exercise' && (
          <div className="chinese-practice-translation-section">
            <label htmlFor="difficulty-select" className="chinese-practice-translation-section-header">
              /difficulty
            </label>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <select
                id="difficulty-select"
                className="chinese-practice-translation-select"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as Difficulty)}
                disabled={isGenerating || isEvaluating}
                style={{ flex: '1', minWidth: '200px' }}
              >
                <option value="HSK 3-4">HSK 3-4</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
              <button
                className="chinese-practice-translation-btn chinese-practice-translation-btn-generate"
                onClick={handleGenerateNew}
                disabled={isGenerating || isEvaluating}
              >
                {isGenerating ? 'Generating...' : 'Generate Exercise'}
              </button>
            </div>
          </div>
        )}

        {/* Show difficulty for past exercise */}
        {viewMode === 'past-exercise' && currentExercise && (
          <div className="chinese-practice-translation-section">
            <div className="chinese-practice-translation-section-header">/difficulty</div>
            <div className="chinese-practice-translation-english-text" style={{ display: 'inline-block', padding: '0.5rem 1rem' }}>
              {currentExercise.difficulty}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isGenerating && (
          <div className="chinese-practice-translation-loading">
            <div className="chinese-spinner-large"></div>
            <p className="chinese-loading-text">Generating exercise...</p>
          </div>
        )}

        {/* Error Message */}
        {error && !isGenerating && !isEvaluating && (
          <div className="chinese-error">
            <p>{error}</p>
          </div>
        )}

        {/* Saving Error Message */}
        {savingError && !isGenerating && !isEvaluating && (
          <div className="chinese-error" style={{ background: '#fff3cd', borderColor: '#ffc107', color: '#856404' }}>
            <p>{savingError}</p>
          </div>
        )}

        {/* Exercise Content */}
        {!isGenerating && (viewMode === 'past-exercise' ? currentExercise : englishText) && (
          <>
            {/* English Paragraph */}
            <div className="chinese-practice-translation-section">
              <div className="chinese-practice-translation-section-header">/english-text</div>
              <div className="chinese-practice-translation-english-text">
                {viewMode === 'past-exercise' && currentExercise ? currentExercise.englishText : englishText}
              </div>
            </div>

            {/* Translation Input - Only show for current exercise, not past exercises */}
            {viewMode !== 'past-exercise' && (
              <div className="chinese-practice-translation-section">
                <div className="chinese-practice-translation-section-header">/your-translation</div>
                <textarea
                  className="chinese-practice-translation-textarea"
                  value={userTranslation}
                  onChange={(e) => setUserTranslation(e.target.value)}
                  placeholder="Enter your Chinese translation here..."
                  rows={8}
                  disabled={isEvaluating || !!evaluationResult}
                />
                <div style={{ marginTop: '0.75rem' }}>
                  <button
                    className="chinese-practice-translation-btn chinese-practice-translation-btn-generate"
                    onClick={handleGenerateAnswer}
                    disabled={isGeneratingAnswer || isEvaluating || isGenerating || !!evaluationResult || !englishText.trim()}
                  >
                    {isGeneratingAnswer ? 'Generating Answer...' : 'Generate Answer'}
                  </button>
                </div>
              </div>
            )}

            {/* Show past exercise translation if viewing past exercise */}
            {viewMode === 'past-exercise' && currentExercise && currentExercise.userTranslation && (
              <div className="chinese-practice-translation-section">
                <div className="chinese-practice-translation-section-header">/your-translation</div>
                <div className="chinese-practice-translation-english-text">
                  {currentExercise.userTranslation}
                </div>
              </div>
            )}

            {/* Generated Answer - Only show for current exercise */}
            {viewMode !== 'past-exercise' && generatedAnswer && (
              <div className="chinese-practice-translation-section">
                <div className="chinese-practice-translation-section-header">/generated-answer</div>
                <div className="chinese-practice-translation-english-text" style={{ color: '#155724', fontWeight: 500 }}>
                  {generatedAnswer.translation}
                </div>
                <div className="chinese-practice-translation-sentence-pinyin" style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  {generatedAnswer.pinyin}
                </div>
              </div>
            )}

            {/* Redo Exercise Button - Only show for past exercises */}
            {viewMode === 'past-exercise' && currentExercise && (
              <div className="chinese-practice-translation-actions">
                <button
                  className="chinese-practice-translation-btn chinese-practice-translation-btn-submit"
                  onClick={handleRedoExercise}
                  disabled={isGenerating || isEvaluating}
                >
                  Redo Exercise
                </button>
              </div>
            )}

            {/* Action Buttons - Only show for current exercise */}
            {viewMode !== 'past-exercise' && (
              <div className="chinese-practice-translation-actions">
                <button
                  className="chinese-practice-translation-btn chinese-practice-translation-btn-submit"
                  onClick={handleSubmit}
                  disabled={isEvaluating || !userTranslation.trim() || !!evaluationResult}
                >
                  {isEvaluating ? 'Evaluating...' : 'Submit'}
                </button>
                <button
                  className="chinese-practice-translation-btn chinese-practice-translation-btn-reset"
                  onClick={handleReset}
                  disabled={isGenerating || isEvaluating || (!userTranslation.trim() && !evaluationResult)}
                >
                  Reset
                </button>
              </div>
            )}

            {/* Evaluation Feedback */}
            {(viewMode === 'past-exercise' ? currentExercise?.evaluationResult : evaluationResult) && (
              <div className="chinese-practice-translation-feedback">
                <div className="chinese-practice-translation-section-header">/feedback</div>
                
                {/* Overall Feedback */}
                <div className="chinese-practice-translation-overall-feedback">
                  <h3 className="chinese-practice-translation-feedback-title">Overall Assessment</h3>
                  <p className="chinese-practice-translation-feedback-text">
                    {viewMode === 'past-exercise' && currentExercise?.evaluationResult 
                      ? currentExercise.evaluationResult.feedback 
                      : evaluationResult?.feedback}
                  </p>
                </div>

                {/* Sentence-by-Sentence Breakdown */}
                <div className="chinese-practice-translation-sentences">
                  <h3 className="chinese-practice-translation-feedback-title">Sentence-by-Sentence Analysis</h3>
                  {(viewMode === 'past-exercise' && currentExercise?.evaluationResult 
                    ? currentExercise.evaluationResult.sentences 
                    : evaluationResult?.sentences || []).map((sentence, index) => (
                    <div key={index} className="chinese-practice-translation-sentence-item">
                      <div className="chinese-practice-translation-sentence-section">
                        <div className="chinese-practice-translation-sentence-label">English:</div>
                        <div className="chinese-practice-translation-sentence-text">{sentence.english}</div>
                      </div>
                      <div className="chinese-practice-translation-sentence-section">
                        <div className="chinese-practice-translation-sentence-label">Correct Translation:</div>
                        <div className="chinese-practice-translation-sentence-text chinese-practice-translation-correct">
                          {sentence.correct}
                        </div>
                        <div className="chinese-practice-translation-sentence-pinyin">{sentence.pinyin}</div>
                      </div>
                      <div className="chinese-practice-translation-sentence-section">
                        <div className="chinese-practice-translation-sentence-label">Your Translation:</div>
                        <div className="chinese-practice-translation-sentence-text">{sentence.user}</div>
                      </div>
                      <div className="chinese-practice-translation-sentence-section">
                        <div className="chinese-practice-translation-sentence-label">Feedback:</div>
                        <div className="chinese-practice-translation-sentence-text chinese-practice-translation-feedback-detail">
                          {sentence.feedback}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Welcome/Empty State */}
        {!isGenerating && !englishText && !error && viewMode === 'current' && (
          <div className="chinese-practice-translation-welcome">
            <h2>Translation Practice</h2>
            <p>Select a difficulty level and start practicing your Chinese translation skills.</p>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default ChinesePracticeTranslation;

